import React, { PureComponent, Fragment } from "react";
import { PropTypes } from "prop-types";
import { connect } from "react-redux";
import {
  Container,
  Grid,
  Header,
  Divider,
  Loader,
  Icon,
  Button
} from "semantic-ui-react";

import SegmentMessage from "../../components/forms/messages/SegmentMessage.jsx";
import ProjectCard from "../../components/projects/ProjectCard.jsx";
import WelcomeMessage from "../../components/dashboard/WelcomeMessage.jsx";
import InviteCard from "../../components/invites/inviteCard/InviteCard.jsx";
import InvoiceCard from "../../components/invoices/InvoiceCard.jsx";
import ErrorBoundary from "../../components/errorBoundaries/ErrorBoundary.jsx";
import LinkWithReset from "../../components/utils/LinkWithReset.jsx";
import SimplePreloader from "../../components/preloaders/SimplePreloader.jsx";

import { ROUTES } from "../../constants/routing.js";
import { AT_TYPES } from "../../constants/entity.js";
import { OPERATIONS } from "../../constants/operations.js";
import { MODES } from "../../components/invites/inviteCard/modes.js";
import {
  INVITES,
  INVOICES,
  PROJECTS
} from "../../state/actions/actionConstants.js";

import { getProjects } from "../../state/reducers/projects/projectsActions.js";
import { getInvoices } from "../../state/reducers/invoices/invoiceActions.js";
import { getInvites } from "../../state/reducers/invites/invitesActions.js";

import inviteSelector from "../../state/reducers/invites/inviteSelector.js";
import invoiceSelector from "../../state/reducers/invoices/invoiceSelector.js";
import endpointSelector from "../../state/reducers/endpoints/endpointSelector.js";

import { selectTestingEnvironment } from "../../state/reducers/config/configSelectors.js";
import { selectCopy } from "../../state/reducers/locale/copySelector.js";
import { openHelpWidget } from "../../state/reducers/helpWidget/helpWidgetActions.js";
import { selectLatestErrorMessagesByRequestType } from "../../state/reducers/api/apiSelectors.js";

class Dashboard extends PureComponent {
  static numProjectCards = 2;
  static numInviteCards = 2;
  static numInvoiceCards = 2;

  static propTypes = {
    projectsIsFetching: PropTypes.bool.isRequired,
    projectsIndex: PropTypes.array,
    accountIsFetching: PropTypes.bool.isRequired,
    pendingEmailVerification: PropTypes.bool.isRequired,
    invitesIsFetching: PropTypes.bool.isRequired,
    invites: PropTypes.array,
    invoicesIsFetching: PropTypes.bool.isRequired,
    invoices: PropTypes.array,
    createProjectAvailability: PropTypes.object.isRequired,
    copy: PropTypes.func.isRequired
  };

  // -- PAGE DATA MANAGEMENT -- //

  componentDidMount() {
    const {
      listInviteAvailability,
      invitesIsFetching,
      getInvites,
      listInvoiceAvailability,
      invoicesIsFetching,
      getInvoices
    } = this.props;

    if (listInviteAvailability.available && !invitesIsFetching) {
      const searchParams = `?marking=pendingSubject&order[createdAt]=desc&itemsPerPage=${Dashboard.numInviteCards}`;
      getInvites(searchParams, { showErrorModal: false });
    }

    if (listInvoiceAvailability.available && !invoicesIsFetching) {
      const searchParams = `?marking=due&itemsPerPage=${Dashboard.numInvoiceCards}`;
      getInvoices(searchParams, { showErrorModal: false });
    }

    this.getData(true);
  }

  getData = () => {
    const { listProjectAvailability, projectsIsFetching, getProjects } =
      this.props;

    if (listProjectAvailability.available && !projectsIsFetching) {
      const searchParams = `?itemsPerPage=${Dashboard.numProjectCards}`;
      getProjects(searchParams, true, false, { showErrorModal: false });
    }
  };

  // -- RENDER MESSAGING -- //

  renderWelcomeMessage = () => {
    const {
      projectsIndex,
      projectsIsFetching,
      testingEnvironment,
      createProjectAvailability: { permitted },
      openHelpWidget
    } = this.props;

    if (
      !permitted &&
      !projectsIsFetching &&
      (!projectsIndex || projectsIndex.length < 1)
    ) {
      return (
        <Grid.Row columns={1}>
          <Grid.Column>
            <WelcomeMessage
              openHelpWidget={openHelpWidget}
              testingEnvironment={testingEnvironment}
            />
          </Grid.Column>
        </Grid.Row>
      );
    }

    return null;
  };

  renderNotifications = () => {
    const { pendingEmailVerification, copy } = this.props;

    if (!pendingEmailVerification) return null;

    return (
      <Grid.Row columns={1}>
        <Grid.Column>
          <SegmentMessage
            title={copy("account.email.pending_notification.title")}
            message={copy("account.email.pending_notification.message")}
            info={true}
            dismissable={false}
            timed={false}
          />
        </Grid.Column>
      </Grid.Row>
    );
  };

  // -- RENDER SECTIONS -- //

  renderProjectCards = () => {
    const {
      projectsIsFetching,
      listProjectAvailability: { available },
      projectsIndex,
      projectErrors,
      copy
    } = this.props;

    if (!available) return null;

    if (projectsIsFetching) {
      return this.renderLoader("dashboard.latest_projects");
    }

    if (!projectsIndex || projectsIndex.length < 1) {
      return (
        <Fragment>
          {this.renderNoneAvailable(
            "dashboard.latest_projects",
            "dashboard.no_projects",
            projectErrors
          )}
          {this.renderCreateProjectButton()}
        </Fragment>
      );
    }

    return (
      <Fragment>
        <Header as="h3">{copy("dashboard.latest_projects")}</Header>
        {projectsIndex.slice(0, Dashboard.numProjectCards).map(id => {
          return <ProjectCard key={id} id={id} small={true} />;
        })}
        <LinkWithReset to={ROUTES.PROJECTS} className={"float-right"}>
          {copy("dashboard.view_all_projects")}
        </LinkWithReset>
        {this.renderCreateProjectButton()}
        <Divider clearing hidden />
      </Fragment>
    );
  };

  renderInvites = () => {
    const {
      invitesIsFetching,
      listInviteAvailability: { available },
      account,
      invites,
      inviteErrors,
      copy
    } = this.props;

    if (!available) return null;

    if (invitesIsFetching) {
      return this.renderLoader("dashboard.latest_invites");
    }

    if (!invites || invites.length < 1) {
      return this.renderNoneAvailable(
        "dashboard.latest_invites",
        "dashboard.no_invites",
        inviteErrors
      );
    }

    return (
      <Fragment>
        <Header as="h3">{copy("dashboard.latest_invites")}</Header>
        {invites.map(invite => {
          const mode = invite.isUser ? MODES.INVITER : MODES.INVITEE;
          return (
            <InviteCard
              key={invite["@id"]}
              invite={invite}
              user={account}
              mode={mode}
            />
          );
        })}
        <LinkWithReset to={ROUTES.INVITES} className={"float-right"}>
          {copy("dashboard.view_all_invites")}
        </LinkWithReset>
        <Divider clearing hidden />
      </Fragment>
    );
  };

  renderInvoices = () => {
    const {
      invoicesIsFetching,
      listInvoiceAvailability: { available },
      invoices,
      invoiceErrors,
      copy
    } = this.props;

    if (!available) return null;

    if (invoicesIsFetching) {
      return this.renderLoader("dashboard.latest_invoices");
    }

    if (!invoices || invoices.length < 1) {
      return this.renderNoneAvailable(
        "dashboard.latest_invoices",
        "dashboard.no_invoices",
        invoiceErrors
      );
    }

    return (
      <Fragment>
        <Header as="h3">{copy("dashboard.latest_invoices")}</Header>
        {invoices.map(invoice => {
          return <InvoiceCard key={invoice["@id"]} data={invoice} />;
        })}
        <p className="mini-text">*{copy(`dashboard.invoices_utc_notice`)}</p>
        <LinkWithReset to={ROUTES.INVOICES} className={"float-right"}>
          {copy("dashboard.view_all_invoices")}
        </LinkWithReset>
        <Divider clearing hidden />
      </Fragment>
    );
  };

  // -- RENDER SECTION EXTRAS -- //

  renderLoader = copyKey => {
    const { copy } = this.props;
    return (
      <Fragment>
        <Header as="h3">{copy(copyKey)}</Header>
        <Loader active inline size="small" />
      </Fragment>
    );
  };

  renderNoneAvailable = (headerCopyKey, bodyCopyKey, errors) => {
    const { copy } = this.props;

    return (
      <Fragment>
        <Header as="h3">{copy(headerCopyKey)}</Header>
        {errors && errors.length > 0 && (
          <SegmentMessage
            title={`${errors[0].title}`}
            message={errors[0].message}
            negative={true}
          />
        )}
        <p>{copy(bodyCopyKey)}</p>
      </Fragment>
    );
  };

  renderCreateProjectButton = () => {
    const {
      createProjectAvailability: { permitted, enabled },
      copy
    } = this.props;

    if (permitted !== true) return null;

    return (
      <Button
        as={LinkWithReset}
        to={ROUTES.PROJECT_WIZARD}
        floated="left"
        className="add-project-button"
        disabled={!enabled}
      >
        <Icon name="plus circle" />
        {copy("projects.add_new_cta")}
      </Button>
    );
  };

  // -- RENDER -- //

  render() {
    const { accountIsFetching } = this.props;

    // early return if dashboard isn't ready to render
    // we need to know about projects for WelcomeMessage display logic
    if (accountIsFetching) {
      return <SimplePreloader />;
    }

    return (
      <Container className="main-responsive">
        <Grid columns={2} relaxed stackable>
          <ErrorBoundary>{this.renderNotifications()}</ErrorBoundary>
          <ErrorBoundary>{this.renderWelcomeMessage()}</ErrorBoundary>
          <ErrorBoundary>
            <Grid.Row>
              <Grid.Column>
                <ErrorBoundary>{this.renderProjectCards()}</ErrorBoundary>
              </Grid.Column>
              <Grid.Column>
                <ErrorBoundary>{this.renderInvites()}</ErrorBoundary>
                <ErrorBoundary>{this.renderInvoices()}</ErrorBoundary>
              </Grid.Column>
            </Grid.Row>
          </ErrorBoundary>
        </Grid>
      </Container>
    );
  }
}

const mapStateToProps = (state, props) => ({
  testingEnvironment: selectTestingEnvironment(state),
  account: state.account.data,
  accountIsFetching: state.account.isFetching,
  pendingEmailVerification: state.account.data.pendingEmailVerification,
  projectsIsFetching: state.projects.isFetching,
  projectsIndex: state.projects.dataIndex,
  invitesIsFetching: state.invites.isFetching,
  invites: inviteSelector.selectPendingInvites(Dashboard.numInviteCards)(
    state,
    props
  ),
  invoicesIsFetching: state.invoices.isFetching,
  invoices: invoiceSelector.selectLatestInvoices(Dashboard.numInvoiceCards)(
    state,
    props
  ),
  createProjectAvailability: endpointSelector.selectOperationAvailability(
    state
  )(AT_TYPES.PROJECT, OPERATIONS.CREATE),
  listProjectAvailability: endpointSelector.selectOperationAvailability(state)(
    AT_TYPES.PROJECT,
    OPERATIONS.LIST
  ),
  listInvoiceAvailability: endpointSelector.selectOperationAvailability(state)(
    AT_TYPES.INVOICE,
    OPERATIONS.LIST
  ),
  listInviteAvailability: endpointSelector.selectOperationAvailability(state)(
    AT_TYPES.INVITE,
    OPERATIONS.LIST
  ),
  inviteErrors: selectLatestErrorMessagesByRequestType(state)(INVITES.GET),
  invoiceErrors: selectLatestErrorMessagesByRequestType(state)(INVOICES.GET),
  projectErrors: selectLatestErrorMessagesByRequestType(state)(PROJECTS.GET),
  copy: selectCopy(state)
});

const mapDispatchToProps = dispatch => ({
  getProjects: (...args) => dispatch(getProjects(...args)),
  getInvites: (...args) => dispatch(getInvites(...args)),
  getInvoices: (...args) => dispatch(getInvoices(...args)),
  openHelpWidget: data => dispatch(openHelpWidget(data))
});

export default connect(mapStateToProps, mapDispatchToProps)(Dashboard);
